import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Handshake, Trophy, Eye } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';


const CodeOfPractice: React.FC<{ content?: any }> = ({ content }) => {
  const { isArabic } = useLanguage();
  const sectionTitle = content?.title || (isArabic ? "السلامة وأفضل الممارسات" : "Safety & Best Practices");
  const sectionDesc = content?.description || (isArabic ? "السلامة هي الأولوية القصوى في قسم النقل في مجموعة حمدان." : "Safety is paramount with HTD...");
  const footerDesc = content?.footerDescription || (isArabic ? "خدمة شركائنا في جميع أنحاء سلطنة عمان بأعلى معايير السلامة والجودة." : "To serve Partners across the Sultanate of Oman...");

  const items = content?.items || [
    {
      iconName: 'Trophy',
      title: isArabic ? 'شرف حمدان' : 'Hamdan Honour',
      description: isArabic ? 'يشرفنا كناقل عماني المساهمة في خدمة الاقتصاد الوطني.' : 'We are honoured as an Omani transporter to be serving the economy.',
      color: 'text-amber-500',
    },
    {
      iconName: 'Handshake',
      title: isArabic ? 'تناغم حمدان' : 'Hamdan Harmony',
      description: isArabic ? 'نعمل على موائمة ممارساتنا تجارياً مع احتياجات عملائنا.' : 'We will harmonise our business practices with your needs.',
      color: 'text-emerald-500',
    },
    {
      iconName: 'Heart',
      title: isArabic ? 'سعادة حمدان' : 'Hamdan Happiness',
      description: isArabic ? 'نحرص دائماً على بيئة عمل آمنة وممتعة.' : 'We are safe and happy always in our work!',
      color: 'text-red-500',
    },
    {
      iconName: 'Eye',
      title: isArabic ? 'نزاهة حمدان' : 'Hamdan Honesty',
      description: isArabic ? 'نلتزم بالشفافية والصدق في كافة تعاملاتنا التجارية.' : 'We will be open and transparent in all of our business dealings',
      color: 'text-blue-500',
    },
  ];

  const iconMap: Record<string, any> = { Trophy, Handshake, Heart, Eye };

  return (
    <section className="py-10  bg-white-50 ">
      <div className="relative z-10 max-w-7xl mx-auto px-6 ">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-dark mb-4">
            {sectionTitle}
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-4xl mx-auto text-[15px] md:text-base text-center">
            {sectionDesc}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {items.map((principle: any, index: number) => {
            const IconComponent = iconMap[principle.iconName] || Trophy;
            return (
              <motion.div
                key={index}
                className="bg-white rounded-2xl shadow-sm border border-gray-50 p-8 flex flex-col items-center text-center transition-all duration-300 h-full"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <div className="mb-6 flex-shrink-0">
                  <IconComponent
                    className={`h-12 w-12 ${
                      principle.color === 'text-yellow-600' ? 'text-amber-500' :
                      principle.color === 'text-green-600' ? 'text-emerald-500' :
                      principle.color === 'text-red-600' ? 'text-red-500' :
                      principle.color === 'text-blue-600' ? 'text-blue-500' :
                      principle.color || 'text-red-500'
                    }`}
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {principle.title}
                </h3>
                <p className="text-gray-500 text-[15px] leading-relaxed flex-grow">
                  {principle.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <p className="text-gray-700 leading-relaxed text-justify md:text-center text-[15px] md:text-base max-w-7xl mx-auto">
          {footerDesc}
        </p>
      </div>
    </section>
  );
};

export default CodeOfPractice;
